// ConfirmDialog (Spec §7 / P7) — the one confirm dialog. Replaces the
// dead-button / fake-success epidemic: a destructive or consequential action
// confirms here, runs the real mutation, shows a pending state, and surfaces
// the true result (success closes; failure stays open with a helpful error and
// a retry). No success message is shown unless the server actually confirmed.

import { useEffect, useState, type ReactNode } from 'react'
import { AlertTriangle, Loader2, X } from 'lucide-react'

interface ConfirmDialogProps {
  open: boolean
  title: string
  /** what will happen — be specific and honest */
  message: ReactNode
  confirmLabel?: string
  cancelLabel?: string
  tone?: 'default' | 'danger'
  /** async action; throw to surface an error and keep the dialog open */
  onConfirm: () => Promise<void> | void
  onClose: () => void
}

export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset transient state whenever the dialog is (re)opened.
  useEffect(() => {
    if (open) {
      setBusy(false)
      setError(null)
    }
  }, [open])

  useEffect(() => {
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape' && !busy) onClose()
    }
    if (open) document.addEventListener('keydown', onEsc)
    return () => document.removeEventListener('keydown', onEsc)
  }, [open, busy, onClose])

  if (!open) return null

  async function handleConfirm() {
    setBusy(true)
    setError(null)
    try {
      await onConfirm()
      // Only close on a genuinely successful result.
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong. Please try again.')
    } finally {
      setBusy(false)
    }
  }

  const danger = tone === 'danger'

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-label={title}>
      <div className="absolute inset-0 bg-slate-900/50" onClick={() => !busy && onClose()} aria-hidden />
      <div className="relative w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
        <div className="flex items-start gap-3">
          {danger && (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600">
              <AlertTriangle size={20} aria-hidden />
            </span>
          )}
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold text-iflow-900">{title}</h2>
            <div className="mt-1 text-sm leading-relaxed text-slate-600">{message}</div>
          </div>
          <button type="button" onClick={() => !busy && onClose()} aria-label="Close" className="text-slate-400 hover:text-slate-600" disabled={busy}>
            <X size={18} />
          </button>
        </div>

        {error && (
          <p role="alert" className="mt-4 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700">
            {error}
          </p>
        )}

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded-lg px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-50"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={busy}
            className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-60 ${
              danger ? 'bg-rose-600 hover:bg-rose-700' : 'bg-iflow-600 hover:bg-iflow-700'
            }`}
          >
            {busy && <Loader2 size={16} className="animate-spin" aria-hidden />}
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
